import React, { useState } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Box, Typography, Tooltip, TextField } from '@mui/material';
import { getCategoryColor, getCapacityColor } from './constants';
import ResizeHandle from './ResizeHandle';

/**
 * Renders a draggable table element on the floor plan canvas.
 *
 * Props:
 *   tableId      – DB table id (number)
 *   layoutData   – { x, y, shape, width, height, rotation }
 *   dbTable      – { id, tableName, capacity, tableType, guests, guestCount }
 *   activeFilter – 'none' | 'side' | 'category'
 *   isSelected   – boolean
 *   isHighlighted – boolean (guest search highlight)
 *   onClick      – () => void
 *   onRename     – (newName: string) => void
 *   onResize     – (w, h) => void
 *   onResizeEnd  – (w, h) => void
 *   onRotate     – (angle: number) => void
 */
export default function TableElement({
  tableId,
  layoutData,
  dbTable,
  zoom = 1,
  activeFilter,
  isSelected,
  isHighlighted,
  highlightedGuestId,
  onClick,
  onRename,
  onResize,
  onResizeEnd,
  onRotate,
}) {
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');

  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id: `table-${tableId}`,
    data: { type: 'CANVAS_ELEMENT', id: tableId, isTable: true },
  });

  // Az asztal egésze is drop-célpont: ha egy vendéget nem pontosan egy üres székre,
  // hanem "valahova az asztalra" dobnak, ez a fallback automatikusan választ szabad
  // széket (lásd SeatingTab handleDragEnd GUEST ága).
  const { setNodeRef: setDropRef, isOver: isGuestOver } = useDroppable({
    id: `table-${tableId}`,
    data: { type: 'CANVAS_ELEMENT', id: tableId, isTable: true },
  });
  const setNodeRef = (node) => { setDragRef(node); setDropRef(node); };

  const { x, y, shape, width, height, rotation = 0 } = layoutData;
  const guests = dbTable?.guests ?? [];
  const capacity = dbTable?.capacity ?? 0;
  const tableName = dbTable?.tableName ?? String(tableId);
  const guestCount = guests.length;

  const capacityColor = getCapacityColor(guestCount, capacity);
  const isRound = shape === 'round';

  // Compute category distribution for color bar
  const categoryGroups = {};
  guests.forEach(g => {
    const cat = g.guestCategory || 'default';
    const key = activeFilter === 'none' ? 'default' : (activeFilter === 'side' ? (cat === 'bride' || cat === 'groom' ? cat : 'default') : cat);
    categoryGroups[key] = (categoryGroups[key] || 0) + 1;
  });
  const segments = Object.entries(categoryGroups);

  const style = {
    position: 'absolute',
    left: x + (transform?.x ?? 0) / zoom,
    top: y + (transform?.y ?? 0) / zoom,
    width,
    height,
    cursor: isDragging ? 'grabbing' : 'grab',
    userSelect: 'none',
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 100 : (isSelected ? 10 : 5),
    borderRadius: isRound ? '50%' : 8,
    background: isGuestOver
      ? '#c8e6c9'
      : isRound ? 'radial-gradient(circle, #e8f5e9 60%, #c8e6c9 100%)' : '#e8f5e9',
    // A keresésnél talált vendég székén villog a jelzés (lásd SeatDots), az asztal
    // nem pulzáló narancs kerettel + fénnyel jelzi, hogy "itt ül" — így messziről is
    // látszik az asztal, a konkrét széket pedig a villogó pötty mutatja.
    border: isSelected
      ? '2px solid #2a5d32'
      : isGuestOver
        ? '2px dashed #2a5d32'
        : isHighlighted
          ? '3px solid #f4a261'
          : '1.5px solid #a5d6a7',
    // Keresési találat: jól látható narancs fény az asztal körül (korábban csak vékony keret volt)
    boxShadow: isSelected
      ? '0 0 0 3px rgba(42,93,50,0.3)'
      : isHighlighted
        ? '0 0 0 5px rgba(244,162,97,0.35), 0 2px 6px rgba(0,0,0,0.12)'
        : '0 2px 6px rgba(0,0,0,0.12)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
    transform: `rotate(${rotation}deg)`,
    transformOrigin: 'center center',
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setNameValue(tableName);
    setEditingName(true);
  };

  const handleNameSubmit = () => {
    setEditingName(false);
    if (nameValue.trim() && nameValue.trim() !== tableName) {
      onRename(nameValue.trim());
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-dnd-draggable="true"
      {...listeners}
      {...attributes}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onDoubleClick={handleDoubleClick}
    >
      {/* Seat dots – positioned radially for round, or linearly for rect */}
      <SeatDots
        tableId={tableId}
        guests={guests}
        capacity={capacity}
        shape={shape}
        width={width}
        height={height}
        activeFilter={activeFilter}
        highlightedGuestId={highlightedGuestId}
      />

      {/* Table label */}
      {editingName ? (
        <TextField
          value={nameValue}
          onChange={(e) => setNameValue(e.target.value)}
          onBlur={handleNameSubmit}
          onKeyDown={(e) => { if (e.key === 'Enter') handleNameSubmit(); if (e.key === 'Escape') setEditingName(false); }}
          autoFocus
          size="small"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            zIndex: 30,
            '& .MuiInputBase-input': { textAlign: 'center', fontSize: 12, p: '2px 6px' },
            width: '80%',
          }}
        />
      ) : (
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            color: '#2a5d32',
            fontSize: shape === 'round' ? 13 : 12,
            textAlign: 'center',
            lineHeight: 1.2,
            pointerEvents: 'none',
            px: 0.5,
          }}
        >
          {tableName}
        </Typography>
      )}

      {/* Capacity indicator */}
      <Typography
        variant="caption"
        sx={{ color: capacityColor, fontSize: 10, fontWeight: 600, pointerEvents: 'none' }}
      >
        {guestCount}/{capacity}
      </Typography>

      {/* Category color bar – only when a color filter is active */}
      {segments.length > 0 && activeFilter !== 'none' && (
        <Box
          sx={{
            position: 'absolute',
            bottom: isRound ? '8%' : 0,
            left: isRound ? '15%' : 0,
            right: isRound ? '15%' : 0,
            height: 5,
            borderRadius: isRound ? 3 : '0 0 8px 8px',
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          {segments.map(([cat, count]) => (
            <Box
              key={cat}
              sx={{ flex: count, bgcolor: getCategoryColor(activeFilter === 'none' ? 'default' : cat) }}
            />
          ))}
        </Box>
      )}

      {/* Resize + rotate handles (only when selected) */}
      {isSelected && (
        <ResizeHandle
          x={x}
          y={y}
          width={width}
          height={height}
          zoom={zoom}
          rotation={rotation}
          uniformScale={isRound}
          onResize={onResize}
          onResizeEnd={onResizeEnd}
          onRotate={onRotate}
        />
      )}
    </Box>
  );
}

// ─── Seat Dots ─────────────────────────────────────────────────────────────────

function SeatDots({ tableId, guests, capacity, shape, width, height, activeFilter, highlightedGuestId }) {
  if (!capacity) return null;

  // Separate guests with a fixed seat from those without (to avoid showing the same
  // guest both at their assigned seat AND at a fallback index position)
  const guestsBySeat = {};
  const unseated = [];
  guests.forEach(g => {
    if (g.seatNumber) guestsBySeat[g.seatNumber] = g;
    else unseated.push(g);
  });
  const dots = Array.from({ length: capacity }, (_, i) => {
    const seatNum = i + 1;
    const guest = guestsBySeat[seatNum] || unseated.shift() || null;
    return { seatNumber: seatNum, guest };
  });

  if (shape === 'round') {
    const cx = width / 2;
    const cy = height / 2;
    const radius = Math.min(width, height) / 2 - 10;

    return (
      <>
        {dots.map(({ seatNumber, guest }, seatIndex) => {
          const angle = (2 * Math.PI * seatIndex) / capacity - Math.PI / 2;
          const cxSeat = cx + radius * Math.cos(angle);
          const cySeat = cy + radius * Math.sin(angle);
          return (
            <SeatDot
              key={seatNumber}
              tableId={tableId}
              seatNumber={seatNumber}
              guest={guest}
              cx={cxSeat}
              cy={cySeat}
              dotSize={12}
              activeFilter={activeFilter}
              highlightedGuestId={highlightedGuestId}
            />
          );
        })}
      </>
    );
  }

  // Rectangular/square: seats around the perimeter
  return (
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {dots.map(({ seatNumber, guest }, seatIndex) => {
        const pos = getRectSeatPosition(seatIndex, capacity, width, height);
        return (
          <SeatDot
            key={seatNumber}
            tableId={tableId}
            seatNumber={seatNumber}
            guest={guest}
            cx={pos.x}
            cy={pos.y}
            dotSize={10}
            activeFilter={activeFilter}
            highlightedGuestId={highlightedGuestId}
          />
        );
      })}
    </Box>
  );
}

// A vizuális pont (10–14px) köré egy nagyobb, láthatatlan hit-area kerül drop-célpontként
// (csak üres székeknél), hogy mobilon/touch-csal is célozható legyen. A pont mérete és
// kinézete változatlan marad.
const SEAT_HIT_SIZE = 28;

function SeatDot({ tableId, seatNumber, guest, cx, cy, dotSize, activeFilter, highlightedGuestId }) {
  const isEmpty = !guest;
  const { setNodeRef, isOver } = useDroppable({
    id: `seat-${tableId}-${seatNumber}`,
    data: { type: 'SEAT', tableId, seatNumber },
    disabled: !isEmpty,
  });

  const color = guest
    ? getCategoryColor(activeFilter === 'none' ? 'default' : (guest.guestCategory || 'default'))
    : (isOver ? '#c8e6c9' : '#fff');
  const isHighlighted = !!guest && (Array.isArray(highlightedGuestId)
    ? highlightedGuestId.includes(guest.id)
    : guest.id === highlightedGuestId);

  return (
    <Tooltip title={<SeatTooltipContent guest={guest} />} arrow placement="top">
      <Box
        ref={setNodeRef}
        sx={{
          position: 'absolute',
          left: cx - SEAT_HIT_SIZE / 2,
          top: cy - SEAT_HIT_SIZE / 2,
          width: SEAT_HIT_SIZE,
          height: SEAT_HIT_SIZE,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto',
          cursor: guest ? 'pointer' : 'copy',
        }}
      >
        <Box
          sx={{
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            bgcolor: color,
            border: isHighlighted
              ? '2px solid #f4a261'
              : isOver
                ? '2px dashed #2a5d32'
                : `1.5px solid ${guest ? '#9e9e9e' : '#c8e6c9'}`,
            transition: 'transform 0.15s',
            zIndex: isHighlighted ? 6 : undefined,
            animation: isHighlighted ? 'seatingPulse 1s ease-in-out 3' : 'none',
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Tooltip>
  );
}

function getRectSeatPosition(index, total, width, height) {
  if (total === 0) return { x: width / 2, y: height / 2 };

  // Distribute seats proportionally per side, centered within each side
  const perimeter = 2 * (width + height);
  const topCount = Math.max(1, Math.round(total * width / perimeter));
  const bottomCount = Math.max(1, Math.round(total * width / perimeter));
  const remaining = Math.max(0, total - topCount - bottomCount);
  const leftCount = Math.floor(remaining / 2);
  const rightCount = remaining - leftCount;

  const positions = [];
  // Top: left → right
  for (let i = 0; i < topCount; i++)
    positions.push({ x: width * (i + 1) / (topCount + 1), y: 0 });
  // Right: top → bottom
  for (let i = 0; i < rightCount; i++)
    positions.push({ x: width, y: height * (i + 1) / (rightCount + 1) });
  // Bottom: right → left
  for (let i = bottomCount - 1; i >= 0; i--)
    positions.push({ x: width * (i + 1) / (bottomCount + 1), y: height });
  // Left: bottom → top
  for (let i = leftCount - 1; i >= 0; i--)
    positions.push({ x: 0, y: height * (i + 1) / (leftCount + 1) });

  return positions[index % positions.length] ?? { x: width / 2, y: height / 2 };
}

function SeatTooltipContent({ guest }) {
  if (!guest) return 'Szabad hely';
  return (
    <Box>
      <Typography variant="caption" fontWeight={700} display="block">
        {guest.lastName} {guest.firstName}
      </Typography>
      {guest.dietaryRestrictions && (
        <Typography variant="caption" display="block" sx={{ color: '#ffcc80' }}>
          Allergia: {guest.dietaryRestrictions}
        </Typography>
      )}
      {guest.guestCategory && (
        <Typography variant="caption" display="block">
          Kat.: {guest.guestCategory}
        </Typography>
      )}
    </Box>
  );
}
